import { useEffect, useRef, useState } from "react";
import { useFieldArray } from "react-hook-form";
import { useLocation, unstable_usePrompt } from "react-router-dom";
import Papa from "papaparse";

import {
  useActivityBlocker,
  useApiErrors,
  useAuth,
  useBalances,
  useDebounce,
  useDisclose,
  useHookForm,
  useMerchantApiMutation,
  useMerchantApiQueryWithParams,
  useNotifications,
  useReactRouterLink,
} from "../../../../hooks";
import {
  IBank,
  ICreateBankTransfer,
  ICreateBankTransferBeneficiary,
  ICreateBatchBankTransfer,
  IFormWithTableProps,
  IHookForm,
  IModalFormWithTableProps,
  ISwAppServiceApi,
  IViewBalance,
  IViewBatchTransfer,
  IViewBankTransferBeneficiary,
  NoMeta,
  ICreateGatewayTransfer,
} from "../../../../interfaces-and-types";
import {
  createBankSlug,
  generateRandomString,
  getApiErrorsByFieldName,
  getUserDepartment,
  globalHandlerUtils,
  returnNullIfEmptyString,
} from "../../../../utils";

// import { View } from "./View";
import { View } from "./View.VIRTUALZED_LIST";
// import { View as MobileView } from "./View.Mobile";
import { batchBankTransferSchema } from "./batchTransferValidationSchema";
import { batchNotificationInterval, batchWebhookType } from "../../../../enums";

const isDevMode = process.env.NODE_ENV === "development";

const ALLOWED_EXTENSIONS = ["csv"];

export type TBatchExcelOrSingleTransfers =
  | "singleBankTransfers"
  | "singleGatewayTransfers"
  | "bankExcelUpload";

interface ICountryAndCurrencySelect {
  country: string;
  currency: string;
}

export interface ICreateBatchBankTransferFormViewProps
  extends IHookForm<ICreateBatchBankTransfer>,
    IModalFormWithTableProps {
  navigateToSingleTransferForm: () => void;

  isLoadingCreateBatchBankTransfer: boolean;
  isLoadingBatchListVerification: boolean;

  showAllOptions: boolean;
  toggleShowAllOptions: () => void;
  isOpenBankCodesForBulkUpload: boolean;
  toggleShowBankCodesForBulkUpload: () => void;
  entriesWithModifiedReferences: number[];

  batchExcelOrSingleTransfers: TBatchExcelOrSingleTransfers;
  setBatchExcelOrSingleTransfers: (name: TBatchExcelOrSingleTransfers) => void;
  handleUploadCsvBatch: (e: any) => void;
  verifyBatchUploadListAndCreateBeneficiaries: () => void;
  clearBatchList: () => void;
  searchRecipientsText: string | null;
  setSearchRecipientsText: (search: string | null) => void;

  isOpenShowPinInput: boolean;
  onOpenShowPinInput: () => void;
  onCloseShowPinInput: () => void;
  combinedVerifyPinInputErrors: string | null;
  onVerifyPinSubmit: (pin: string) => boolean;
  onVerifyPinCorrect: (/*pin: string*/) => boolean;

  isLoadingBalances: boolean;
  balances: IViewBalance[];

  formValues: ICreateBatchBankTransfer;
  isDirty: boolean;

  // bank transfers
  bankTransfersFieldArray: ICreateBatchBankTransfer["transfers"];
  appendNewBankTransfer: (
    newBankTransferFormValues: ICreateBankTransfer
  ) => void;
  removeBankTransferByIndex: (index: number) => void;
  updateBankTransferByIndex: (
    index: number,
    updatedTransfer: ICreateBankTransfer
  ) => void;
  toggleBankTransferBatchEntryEditMode: (index: number) => void;

  // gateway transfers
  gatewayTransfersFieldArray: ICreateBatchBankTransfer["gateway_transfers"];
  appendNewGatewayTransfer: (
    newGatewayTransferFormValues: ICreateGatewayTransfer
  ) => void;
  removeGatewayTransferByIndex: (index: number) => void;
  updateGatewayTransferByIndex: (
    index: number,
    updatedTransfer: ICreateGatewayTransfer
  ) => void;
  toggleGatewayTransferBatchEntryEditMode: (index: number) => void;
}

const PIN_FIELD_NAME = "pin";

const unpermittedUrlUnsafeCharsRegex = /[^a-zA-Z0-9._|-]/; // /[^a-zA-Z0-9@._|-]/;

// upsert beneficiaries for transfers with bank details
const handleUpdateTransferBeneficiary = (
  data: ISwAppServiceApi<IViewBankTransferBeneficiary>,
  index: number,
  transferInList: ICreateBankTransfer,
  updateTransfer: (index: number, data: ICreateBankTransfer) => void
) => {
  // set the beneficiary id
  if (!data.is_error) {
    const beneficiary = data.data;

    updateTransfer(index, {
      ...transferInList,
      // set fields
      beneficiary_id: beneficiary.id,
      bank_code: beneficiary.bank_code,
      account_number: beneficiary.account_number,
      fullNameHelper: beneficiary.full_name,
      bankNameHelper: beneficiary.bank_name,
      accountNameHelper: beneficiary.account_name,
      // set status
      isBeneficiaryResolved: true,
      isLoadingResolveBeneficiary: false,
      isErrorResolveBeneficiary: false,
      // xxxxxxx: xxxxxxx,
    });

    // setValue(`transfers.${index}`, {
    //   ...trsf,
    //   // set fields
    //   beneficiary_id: beneficiary.id,
    //   bank_code: beneficiary.bank_code,
    //   account_number: beneficiary.account_number,
    //   fullNameHelper: beneficiary.full_name,
    //   bankNameHelper: beneficiary.bank_name,
    //   accountNameHelper: beneficiary.account_name,
    //   // set status
    //   isBeneficiaryResolved: true,
    //   isLoadingResolveBeneficiary: false,
    //   isErrorResolveBeneficiary: false,
    //   // xxxxxxx: xxxxxxx,
    // })
  } else {
    updateTransfer(index, {
      ...transferInList,
      // set status
      isBeneficiaryResolved: false,
      isLoadingResolveBeneficiary: false,
      isErrorResolveBeneficiary: true,
      // xxxxxxx: xxxxxxx,
    });

    // setValue(`transfers.${index}`, {
    //   ...trsf,
    //   // set status
    //   isBeneficiaryResolved: false,
    //   isLoadingResolveBeneficiary: false,
    //   isErrorResolveBeneficiary: true,
    //   // xxxxxxx: xxxxxxx,
    // })
  }
};

export const CreateBatchBankTransferForm = (
  props: IFormWithTableProps & {
    goBackOnClose: boolean; // NOTE: used for on-the-fly product creation eg while creating a payment link
  }
) => {
  const { active_tenant_id, staff_departments } = useAuth();
  const { navigate, navigateToRoutePreserveParams } = useReactRouterLink();
  const location = useLocation();

  const notificationsHandler = useNotifications();

  const { isOpen: showAllOptions, onToggle: toggleShowAllOptions } =
    useDisclose();

  const {
    isOpen: isOpenBankCodesForBulkUpload,
    onToggle: toggleShowBankCodesForBulkUpload,
  } = useDisclose();

  const [batchExcelOrSingleTransfers, setBatchExcelOrSingleTransfers] =
    useState<TBatchExcelOrSingleTransfers>("singleBankTransfers");

  const [searchRecipientsText, setSearchRecipientsText] = useState<
    string | null
  >(null);

  let timeoutId = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const activeStaffDept = getUserDepartment(
      active_tenant_id,
      staff_departments
    );

    if (!activeStaffDept?.has_pin) {
      timeoutId.current = setTimeout(
        () =>
          window.confirm(
            // "PIN Setup",
            "You have not set a transfer PIN. It is required before you can initiate transfers from the dashboard. Would you like to do that now?"
          ) && navigate("/settings/create-transfer-pin"),
        1500
      );
    }

    return () => timeoutId && clearTimeout(timeoutId.current);
  }, [active_tenant_id]);

  const {
    isLoadingBalances,
    activeBalanceCurrency,
    balances,
    refreshBalances,
  } = useBalances();

  const closeModal = () => {
    if (
      (csvFileUploadComplete || !!bankTransfers.length) &&
      !isSuccessSaveBankTransfer
    ) {
      if (
        !window.confirm(
          "Your batched transfers have not been sent. Are you sure you want to close the page?"
        )
      )
        return;
    }

    if (props.goBackOnClose) navigate(-1);
    else
      navigateToRoutePreserveParams(
        location.pathname.replace(/\/create(\/single)?/g, "")
      );
  };

  // const downloadCsvTemplate = () => {
  //   const downloadUrl = window.URL.createObjectURL(
  //     // new Blob([response.data], { type: contentType })
  //     '/public/templates/switchapp-bulk-transfer - TEMPLATE.csv'
  //   );

  //   const link = document.createElement("a");

  //   link.href = downloadUrl;
  //   link.setAttribute("download", fileDownloadName); // any other extension
  //   document.body.appendChild(link);
  //   link.click();
  //   link.remove();
  // }

  const {
    isSuccess: isSuccessSaveBankTransfer,
    isLoading: isLoadingCreateBatchBankTransfer,
    isError: isErrorSaveBankTransfer,
    mutate: saveBankTransfer,
  } = useMerchantApiMutation<ICreateBatchBankTransfer, IViewBatchTransfer>(
    "createBatchBankTransfer",
    ["getBatchList"],
    {
      timeout: 1000 * 60 * 10, // 10 mins timeout to allow for batch verification
    }
  ).usePostMutation;

  useEffect(() => {
    if (isSuccessSaveBankTransfer) {
      reset(); // clear form

      refreshBalances(); // refresh transfers table
      props.refreshTable();

      closeModal();
    }
  }, [isSuccessSaveBankTransfer]);

  useEffect(() => {
    if (isErrorSaveBankTransfer) {
      setValue("pin", "");
    }
  }, [isErrorSaveBankTransfer]);

  const initialFormValues: ICreateBatchBankTransfer = {
    batch_reference: `SWAPP-BATCH-${generateRandomString(15)}`,
    title: null,
    pin: "",
    description: null,
    source_currency: activeBalanceCurrency || "NGN",
    break_on_validation_failure: true,
    scheduled_date: null,
    batch_webhook_type: batchWebhookType.singleOnly,
    batch_notification_interval: batchNotificationInterval.hundredPercent,
    transfers: [],
    gateway_transfers: [],
  };

  const handleApiSubmit = (data: ICreateBatchBankTransfer) => {
    saveBankTransfer(data);
  };

  const handleError = (errors: any) => globalHandlerUtils.handleError(errors);

  const {
    onFormSubmit,
    errors,
    register,
    reset,
    control,
    watch,
    setValue,
    isDirty,
    isValid,
  } = useHookForm({
    initialFormValues,
    validationSchema: batchBankTransferSchema,
    onValid: handleApiSubmit,
    onInvalid: handleError,
  });

  const formValues = watch();

  const { apiErrors } = useApiErrors();

  const shouldShowAllOptions =
    (Object.keys(errors).length > 0 || Object.keys(apiErrors).length > 0) &&
    !showAllOptions;

  if (shouldShowAllOptions) {
    toggleShowAllOptions();
    // navigateToTop();

    console.warn(
      "shouldShowAllOptions Errors: ",
      Object.keys(errors).length > 0 ? errors : apiErrors
    );
  }

  //Error: useBlocker must be used within a data router.  See https://reactrouter.com/routers/picking-a-router.
  // unstable_usePrompt({
  //   when: isDirty,
  //   message:
  //     "Are you sure you want to leave? Your changes have not been submitted!",
  // });

  // BANK TRANSFERS FIELD ARRAY
  const {
    fields: bankTransfers,
    append: appendBankTransfer,
    remove: removeBankTransfer,
    update: updateBankTransfer,
    // replace: replaceBankTransfer,
  } = useFieldArray({ name: "transfers", control });

  const appendNewBankTransfer = (
    newSingleBankTransferFormValues: ICreateBankTransfer
  ) => {
    appendBankTransfer(newSingleBankTransferFormValues);

    if (formValues.source_currency === "")
      setValue("source_currency", newSingleBankTransferFormValues.currency);
  };

  const removeBankTransferByIndex = (index: number) => {
    // alert("removing trsf at index: " + index);

    if (
      window.confirm(
        "Are you sure you want to remove this recipient from the list?"
      )
    )
      removeBankTransfer(index);
  };

  const updateBankTransferByIndex = (
    index: number,
    updatedTransfer: ICreateBankTransfer
  ) => {
    if (
      window.confirm(
        "Are you sure you want to update transfer details for this recipient?"
      )
    )
      updateBankTransfer(index, {
        ...updatedTransfer,
        isEditModeHelper: false,
      });
  };

  // GATEWAY TRANSFERS FIELD ARRAY
  const {
    fields: gatewayTransfers,
    append: appendGatewayTransfer,
    remove: removeGatewayTransfer,
    update: updateGatewayTransfer,
    // replace: replaceGatewayTransfer,
  } = useFieldArray({ name: "gateway_transfers", control });

  const appendNewGatewayTransfer = (
    newSingleGatewayTransferFormValues: ICreateGatewayTransfer
  ) => {
    appendGatewayTransfer(newSingleGatewayTransferFormValues);

    if (formValues.source_currency === "")
      setValue("source_currency", newSingleGatewayTransferFormValues.currency);
  };

  const removeGatewayTransferByIndex = (index: number) => {
    // alert("removing trsf at index: " + index);

    if (
      window.confirm(
        "Are you sure you want to remove this recipient from the list?"
      )
    )
      removeGatewayTransfer(index);
  };

  const updateGatewayTransferByIndex = (
    index: number,
    updatedTransfer: ICreateGatewayTransfer
  ) => {
    if (
      window.confirm(
        "Are you sure you want to update transfer details for this recipient?"
      )
    )
      updateGatewayTransfer(index, {
        ...updatedTransfer,
        isEditModeHelper: false,
      });
  };

  const debouncePeriodElapsed = useDebounce();
  const bankTransferCountry = "NG"; // TODO: provide method of updating this value

  const { isLoading: isLoadingBankList, data: bankList } =
    useMerchantApiQueryWithParams<ICountryAndCurrencySelect, IBank[], NoMeta>(
      [
        "getBanks",
        {
          country: bankTransferCountry,
          currency: formValues.source_currency,
        },
      ],
      {
        enabled:
          debouncePeriodElapsed &&
          // !!formValues.country &&
          !!formValues.source_currency,
      }
    );

  // It state will contain the error when
  // correct file extension is not used
  const { setBlocked, clearBlocked } = useActivityBlocker();

  const [csvFileUploadLoading, setCsvFileUploadLoading] = useState(false);
  const [csvFileUploadComplete, setCsvFileUploadComplete] = useState(false);
  const [isLoadingBatchListVerification, setLoadingBatchListVerification] =
    useState(false);
  const [csvUploadError, setCsvUploadError] = useState("");

  const entriesWithModifiedReferences = useRef<number[]>([]);

  useEffect(() => {
    if (csvFileUploadComplete) {
      if (entriesWithModifiedReferences.current.length) {
        notificationsHandler.showNotification({
          ariaLabel: "warning notification",
          title: "WARNING",
          kind: "error",
          message:
            "NOTE: Some transfer entry references have been modified in order to remove invalid characters!",
          statusIconDescription: "notification",
          timeout: 10000,
        });

        alert(
          "NOTE: Some transfer entry references have been modified in order to remove invalid characters!"
        );

        console.warn(
          "entriesWithModifiedReferences: ",
          entriesWithModifiedReferences
        );
      }

      setLoadingBatchListVerification(true);
      verifyBatchUploadListAndCreateBeneficiaries();
    }
  }, [csvFileUploadComplete]);

  // useEffect(() => {
  //   if (csvFileUploadLoading)
  //     setBlocked("Uploading csv file. Please wait. This may take a while...");
  //   else clearBlocked();
  // }, [csvFileUploadLoading]);

  // useEffect(() => {
  //   if (isLoadingBatchListVerification)
  //     setBlocked(
  //       "Verifying beneficiaries in the list. Please wait. This may take a while..."
  //     );
  //   else clearBlocked();
  // }, [isLoadingBatchListVerification]);

  const handleUploadCsvBatch = (e: any) => {
    setCsvUploadError("");
    setCsvFileUploadLoading(true);
    setCsvFileUploadComplete(false);

    // Check if user has entered the file
    if (e.target.files.length) {
      const inputFile = e.target.files[0];

      // Check the file extensions, if it not
      // included in the allowed extensions
      // we show the error
      const fileExtension = inputFile?.type.split("/")[1];
      if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
        setCsvUploadError(
          "Please upload a csv file using the template provided"
        );
        setCsvFileUploadLoading(false);

        return;
      }

      // If input type is correct set the state
      handleParseCsv(inputFile);
      setCsvFileUploadLoading(false);
    }
  };

  const handleParseCsv = (csvFile: any) => {
    // If user clicks the parse button without
    // a file we show a error
    if (!csvFile) return setCsvUploadError("Enter a valid file");

    entriesWithModifiedReferences.current = []; // reset invalid ref entries list

    try {
      Papa.parse<string[] /*ICsvSingleTransfers*/>(csvFile, {
        // header: true, // do not read the header & use it to convert to JSON
        worker: true, // use a web worker so that the page doesn't hang up
        complete({ data }) {
          setValue("transfers", [
            // ...transfers,
            ...data
              .slice(1)
              .map((entry, index) => {
                const bankCodeOrSlugProvided = !!entry[2] && entry[2] !== "";
                const hasSlug =
                  bankCodeOrSlugProvided && entry[2].includes("--");
                const bankCode = hasSlug
                  ? bankList?.find(
                      (bank) =>
                        createBankSlug(
                          bank.bank_name,
                          bankTransferCountry,
                          formValues.source_currency
                        ) === entry[2]?.trim()
                    )?.bank_code
                  : entry[2]
                      ?.trim()
                      // .replace('"', "").replace('="', "")
                      .replace('="', "") // if bank codes were copied with apostrophe-prefix
                      .replace("'", "")
                      .replace('"', "")
                      .padStart(3, "0");

                const entryContainsInvalidRefChars =
                  unpermittedUrlUnsafeCharsRegex.test(entry[4]?.trim());
                if (entryContainsInvalidRefChars)
                  entriesWithModifiedReferences.current = [
                    ...entriesWithModifiedReferences.current,
                    index,
                  ];

                return {
                  amount: Number(
                    entry[0]?.trim().replace(",", "").replace(" ", "") // remove thousands separator & spaces
                  ),
                  beneficiary_id: entry[1]?.trim(),
                  bank_code: bankCode,
                  account_number: entry[3]
                    ?.trim()
                    .replace('="', "") // if account_numbers were copied with apostrophe-prefix
                    .replace("'", "")
                    .replace('"', "")
                    .padStart(10, "0"),
                  reference:
                    entry[4] !== ""
                      ? entry[4]
                          ?.trim()
                          .replace(" ", "-")
                          .replace(/[^a-zA-Z0-9._|-]+/g, "-")
                      : `BULK-ENTRY-${generateRandomString(15)}`,
                  description: entry[5],
                  beneficiaryName: entry[6]?.trim(),
                  beneficiaryEmail: entry[7]?.trim(),
                  country: entry[8] !== "" ? entry[8]?.trim() : "NG",
                  currency: entry[9] !== "" ? entry[9]?.trim() : "NGN",

                  isBeneficiaryResolved: false,
                  isLoadingResolveBeneficiary: true,
                  isErrorResolveBeneficiary: false,
                } as ICreateBankTransfer;
              })
              .filter((entry) => entry.amount > 0),
          ]);

          setCsvFileUploadComplete(true); // initiate beneficiary verification

          // console.log("csvFileTransfers: ", csvFileTransfers);
        },
      });
    } catch (error) {
      setCsvUploadError(
        "An error occured while attempting to read the csv file."
      );
    }
  };

  const clearBatchList = () => {
    if (
      window.confirm(
        "Are you sure you want to remove all transfers in this list?"
      )
    ) {
      setValue("transfers", []);
      setValue("gateway_transfers", []);

      setCsvFileUploadComplete(false);
    }
  };

  const {
    // isSuccess: isSuccessFetchBankTransferBeneficiary,
    // mutate: fetchBeneficiary,
    mutateAsync: fetchBeneficiaryAsync,
  } = useMerchantApiMutation<
    ICreateBankTransferBeneficiary,
    IViewBankTransferBeneficiary
  >("getBankTransferBeneficiaryById", null, {
    hideResponseMessage: true,
  }).useGetQueryHandler;

  const {
    // isSuccess: isSuccessCreateBankTransferBeneficiary,
    // mutate: createBeneficiary,
    mutateAsync: createBeneficiaryAsync,
  } = useMerchantApiMutation<
    ICreateBankTransferBeneficiary,
    IViewBankTransferBeneficiary
  >("createBankTransferBeneficiary", null, {
    hideResponseMessage: true,
    ignoreActivityBlocker: true,
  }).usePostMutation;

  const verifyBatchUploadListAndCreateBeneficiaries = async () => {
    // NOTE: do not filter as it would lead to INVALID array indices for the whole list
    // transfers.forEach(async (trsf, index) => {
    for (let index = 0; index < bankTransfers.length; index++) {
      const trsf = bankTransfers[index];

      // console.log(
      //   `running verification for index: ${index} => time: ${new Date().getTime()}`
      // );
      // console.time(`verification-${index}`);

      try {
        if (trsf.isBeneficiaryResolved) return;
        else if (trsf.beneficiary_id) {
          // retrieve beneficiary details for existing beneficiaries
          const existingBeneficiarySwApiResp = await fetchBeneficiaryAsync(
            (templateQueryUrl: string) =>
              templateQueryUrl.replace("id", trsf.beneficiary_id!)
          );

          handleUpdateTransferBeneficiary(
            existingBeneficiarySwApiResp,
            index,
            trsf,
            updateBankTransfer
          );
        } else {
          const resp = await createBeneficiaryAsync({
            country: trsf.country,
            currency: trsf.currency,
            account_number: trsf.account_number,
            bank_code: trsf.bank_code,
            full_name: returnNullIfEmptyString(trsf.beneficiaryName),
            email: returnNullIfEmptyString(trsf.beneficiaryEmail),
          });

          // use former after fixing double ServiceResonseApi<> wrapper
          // handleUpdateTransferBeneficiary(resp,index,trsf,updateTransfer);
          handleUpdateTransferBeneficiary(
            resp.data as any as ISwAppServiceApi<IViewBankTransferBeneficiary>,
            index,
            trsf,
            updateBankTransfer
          );
        }
      } catch (error) {
        // set error for failures
        updateBankTransfer(index, {
          ...trsf,
          // set status
          isBeneficiaryResolved: false,
          isLoadingResolveBeneficiary: false,
          isErrorResolveBeneficiary: true,
          // xxxxxxx: xxxxxxx,
        });
      }

      // console.log(
      //   `finished verification check for index: ${index} => time: ${new Date().getTime()}`
      // );
      // console.timeEnd(`verification-${index}`);
    }

    setLoadingBatchListVerification(false);
  };

  const toggleBankTransferBatchEntryEditMode = (index: number) => {
    // switch to bank mode so modal will appear
    setBatchExcelOrSingleTransfers("singleBankTransfers");

    updateBankTransfer(index, {
      ...bankTransfers[index],
      isEditModeHelper: !bankTransfers[index].isEditModeHelper,
    });
  };

  const toggleGatewayTransferBatchEntryEditMode = (index: number) => {
    // switch to gateway mode so modal will appear
    setBatchExcelOrSingleTransfers("singleGatewayTransfers");

    updateGatewayTransfer(index, {
      ...gatewayTransfers[index],
      isEditModeHelper: !gatewayTransfers[index].isEditModeHelper,
    });
  };

  // const updateTempEditModeAmount = (
  //   index: number,
  //   tempEditModeAmount: number
  // ) =>
  //   updateBankTransfer(index, {
  //     ...bankTransfers[index],
  //     tempEditModeAmount,
  //   });

  const navigateToSingleTransferForm = () => {
    navigate(`single`);
  };

  useEffect(() => refreshBalances(), []);

  // enter new PIN
  const pinInputApiErrorMessage =
    getApiErrorsByFieldName(PIN_FIELD_NAME, apiErrors) || "";
  const hookFormErrorMessage =
    errors[PIN_FIELD_NAME] && errors[PIN_FIELD_NAME]?.["message"]?.toString();

  const combinedVerifyPinInputErrors =
    hookFormErrorMessage || pinInputApiErrorMessage || csvUploadError || null;

  const {
    isOpen: isOpenShowPinInput,
    onClose: onCloseShowPinInput,
    onOpen: onOpenShowPinInput,
  } = useDisclose();

  const checkOngoingEditsBeforeOpenPinInput = () => {
    if (bankTransfers.find((trsf) => trsf.isEditModeHelper)) {
      alert(
        "You have ongoing edits to transfer entries. Save changes before proceeding."
      );
      return;
    }

    onOpenShowPinInput();
  };

  const onVerifyPinSubmit = (pin: string): boolean => {
    setValue(PIN_FIELD_NAME, pin);
    return true;
  };

  const onVerifyPinCorrect = (): boolean => {
    onFormSubmit();

    // if (!isValid && isDevMode)
    //   alert("Form is invalid: " + JSON.stringify(errors));

    if (!isValid) return false;

    onCloseShowPinInput();
    return true;
  };

  return (
    <View
      {...props}
      isLoadingCreateBatchBankTransfer={isLoadingCreateBatchBankTransfer}
      isLoadingBatchListVerification={isLoadingBatchListVerification}
      showAllOptions={showAllOptions}
      toggleShowAllOptions={toggleShowAllOptions}
      isOpenBankCodesForBulkUpload={isOpenBankCodesForBulkUpload}
      toggleShowBankCodesForBulkUpload={toggleShowBankCodesForBulkUpload}
      entriesWithModifiedReferences={entriesWithModifiedReferences.current}
      // singles or excel batch upload
      batchExcelOrSingleTransfers={batchExcelOrSingleTransfers}
      setBatchExcelOrSingleTransfers={setBatchExcelOrSingleTransfers}
      handleUploadCsvBatch={handleUploadCsvBatch}
      clearBatchList={clearBatchList}
      verifyBatchUploadListAndCreateBeneficiaries={
        verifyBatchUploadListAndCreateBeneficiaries
      }
      searchRecipientsText={searchRecipientsText}
      setSearchRecipientsText={setSearchRecipientsText}
      // pin input management
      isOpenShowPinInput={isOpenShowPinInput}
      onOpenShowPinInput={checkOngoingEditsBeforeOpenPinInput}
      onCloseShowPinInput={onCloseShowPinInput}
      combinedVerifyPinInputErrors={combinedVerifyPinInputErrors}
      onVerifyPinSubmit={onVerifyPinSubmit}
      onVerifyPinCorrect={onVerifyPinCorrect}
      // others
      isModalOpen
      closeModal={closeModal}
      navigateToSingleTransferForm={navigateToSingleTransferForm}
      isLoadingBalances={isLoadingBalances}
      balances={balances}
      control={control}
      formValues={formValues}
      isDirty={isDirty}
      // bank transfer field array
      bankTransfersFieldArray={bankTransfers}
      appendNewBankTransfer={appendNewBankTransfer}
      removeBankTransferByIndex={removeBankTransferByIndex}
      updateBankTransferByIndex={updateBankTransferByIndex}
      toggleBankTransferBatchEntryEditMode={
        toggleBankTransferBatchEntryEditMode
      }
      // gateway transfer field array
      gatewayTransfersFieldArray={gatewayTransfers}
      appendNewGatewayTransfer={appendNewGatewayTransfer}
      removeGatewayTransferByIndex={removeGatewayTransferByIndex}
      updateGatewayTransferByIndex={updateGatewayTransferByIndex}
      toggleGatewayTransferBatchEntryEditMode={
        toggleGatewayTransferBatchEntryEditMode
      }
      handleSubmit={onFormSubmit}
      register={register}
      errors={errors}
    />
  );
};
